import { useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  FileDown,
  FileText,
  RefreshCw,
  RotateCcw,
  Send,
  Trash2,
} from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  MetricCard,
  MetricStrip,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import {
  BASE,
  isWorking,
  useDeleteQuestionnaire,
  useQuestionnaire,
  useQuestionnaireAction,
} from '../api/queries'
import type { QuestionnaireItem } from '../api/types'
import { ItemCard } from '../components/ItemCard'
import { LibraryPickerDrawer } from '../components/LibraryPickerDrawer'
import { STAGE_META, formatDate, isOverdue } from '../utils/meta'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

type Filter = 'attention' | 'all' | 'unanswered'

function openFile(path: string) {
  window.open(`${API_BASE}${path}`, '_blank', 'noopener')
}

/** /regulatory/questionnaires/$questionnaireId — review the answers, approve. */
export function QuestionnaireDetailPage() {
  const { questionnaireId } = useParams({ strict: false }) as { questionnaireId?: string }
  const navigate = useNavigate()
  const { data: q, isLoading, isError, error, refetch } = useQuestionnaire(questionnaireId ?? null)
  const action = useQuestionnaireAction()
  const remove = useDeleteQuestionnaire()
  const [filter, setFilter] = useState<Filter | null>(null)
  const [picking, setPicking] = useState<QuestionnaireItem | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const items = useMemo(() => q?.items ?? [], [q])
  const attention = items.filter((i) => i.needs_review)
  // Default view: what needs a person, while there is any.
  const effective: Filter =
    filter ?? (q?.stage === 'in_review' && attention.length ? 'attention' : 'all')
  const shown =
    effective === 'attention'
      ? attention
      : effective === 'unanswered'
        ? items.filter((i) => i.status === 'unanswered')
        : items

  if (!questionnaireId) return null
  if (isError) {
    return (
      <div className="p-6">
        <QueryErrorState error={error} onRetry={() => void refetch()} subject="questionnaire" />
      </div>
    )
  }
  if (isLoading || !q) return null

  const canEdit = q.can_edit ?? false
  const editable = canEdit && q.stage === 'in_review'
  const stage = STAGE_META[q.stage]
  const spiPdf = q.output_documents.find((o) => o.kind === 'spi_pdf')
  const filledSource = q.output_documents.find((o) => o.kind === 'filled_source')
  const run = (a: 'retry' | 'rematch' | 'approve' | 'reopen' | 'sent', done: string) =>
    action.mutate(
      { id: q.id, action: a },
      {
        onSuccess: (res) => {
          const changes = res?.library_changes
          toast.success(
            changes
              ? `${done} — library: ${changes.new} added, ${changes.update} updated, ${changes.variant} new wordings`
              : done,
          )
        },
        onError: (err) => toast.danger(err instanceof Error ? err.message : 'Something went wrong'),
      },
    )

  const meta = [
    q.requester_name,
    q.form_reference,
    `Received ${formatDate(q.received_on)}`,
    q.due_on
      ? `Due ${formatDate(q.due_on)}${isOverdue(q.due_on, q.stage) ? ' (overdue)' : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand title={q.title} description={meta}>
        <div className="ml-auto flex items-center gap-3">
          <Chip variant={stage.variant} dot>
            {stage.label}
          </Chip>
          {q.mode === 'import' ? <Chip>Library import</Chip> : null}
          {q.counts.total ? (
            <MetricStrip variant="inline">
              <MetricCard
                variant="inline"
                label="From library"
                value={q.counts.reused.toLocaleString()}
              />
              <MetricCard
                variant="inline"
                label="To check"
                value={q.counts.needs_review.toLocaleString()}
              />
              <MetricCard
                variant="inline"
                label="Unanswered"
                value={q.counts.unanswered.toLocaleString()}
              />
            </MetricStrip>
          ) : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" onClick={() => navigate({ to: '/regulatory/questionnaires' })}>
            <ArrowLeft size={14} /> Questionnaires
          </Button>
          <Button variant="ghost" onClick={() => openFile(`${BASE}/${q.id}/source`)}>
            <FileText size={14} /> Original
          </Button>
          {spiPdf ? (
            <Button
              variant="secondary"
              onClick={() => openFile(`${BASE}/${q.id}/outputs/${spiPdf.document_id}`)}
            >
              <FileDown size={14} /> SPI response PDF
            </Button>
          ) : null}
          {filledSource ? (
            <Button
              variant="secondary"
              title={
                filledSource.skipped
                  ? `${filledSource.placed} answers are in the form; ${filledSource.skipped} had no place in it — they are in the SPI response PDF`
                  : `All ${filledSource.placed} answers are in the form`
              }
              onClick={() => openFile(`${BASE}/${q.id}/outputs/${filledSource.document_id}`)}
            >
              <FileDown size={14} /> Their form, filled in
              {filledSource.skipped ? ` (${filledSource.skipped} left out)` : ''}
            </Button>
          ) : null}
        </ToolbarLeft>
        <ToolbarRight>
          {q.stage === 'failed' ? (
            <Button
              variant="primary"
              loading={action.isPending}
              onClick={() => run('retry', 'Reading it again')}
            >
              <RotateCcw size={14} /> Retry
            </Button>
          ) : null}
          {editable ? (
            <>
              <Button
                variant="ghost"
                loading={action.isPending && action.variables?.action === 'rematch'}
                onClick={() => run('rematch', 'Matched to the library again')}
                title="Match the open questions to the library again, e.g. after adding answers"
              >
                <RefreshCw size={14} /> Re-match
              </Button>
              <Button
                variant="primary"
                loading={action.isPending && action.variables?.action === 'approve'}
                disabled={q.mode === 'new' && attention.length > 0}
                title={
                  q.mode === 'new' && attention.length
                    ? `${attention.length} answers still need review`
                    : 'Save the answers to the library'
                }
                onClick={() => run('approve', 'Approved')}
              >
                <CheckCircle2 size={14} /> {q.mode === 'import' ? 'Add to library' : 'Approve'}
              </Button>
            </>
          ) : null}
          {canEdit && (q.stage === 'approved' || q.stage === 'sent') ? (
            <Button
              variant="ghost"
              loading={action.isPending}
              onClick={() => run('reopen', 'Reopened for review')}
            >
              <RotateCcw size={14} /> Reopen
            </Button>
          ) : null}
          {q.mode === 'new' && q.stage === 'approved' ? (
            <Button
              variant="primary"
              loading={action.isPending}
              onClick={() => run('sent', 'Marked sent')}
            >
              <Send size={14} /> Mark sent
            </Button>
          ) : null}
          {canEdit && q.stage !== 'approved' && q.stage !== 'sent' ? (
            <Button
              variant="ghost"
              iconOnly
              aria-label="Delete questionnaire"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={14} />
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          {q.stage === 'failed' ? (
            <p className="rounded-md border border-danger-border bg-danger-bg p-3 text-sm text-danger-text">
              {q.error_message || 'Reading this questionnaire failed.'} Retry to read it again.
            </p>
          ) : null}
          {q.extraction_report.draft_error ? (
            <p className="rounded-md border border-warning-border bg-warning-bg p-3 text-sm text-warning-text">
              {q.extraction_report.draft_error}
            </p>
          ) : null}
          {q.extraction_report.match_error ? (
            <p className="rounded-md border border-warning-border bg-warning-bg p-3 text-sm text-warning-text">
              {q.extraction_report.match_error}
            </p>
          ) : null}
          {q.extraction_report.notes ? (
            <p className="rounded-md border border-subtle bg-surface p-3 text-sm text-secondary">
              Note from reading the document: {q.extraction_report.notes}
            </p>
          ) : null}
          {q.mode === 'import' && q.stage === 'in_review' ? (
            <p className="rounded-md border border-info-border bg-info-bg p-3 text-sm text-info-text">
              These are SPI's answers as written on the form. Check anything marked, set what each
              answer should do in the library, then Add to library.
            </p>
          ) : null}

          {isWorking(q.stage) ? (
            <EmptyState
              variant="no-data"
              title={
                q.stage === 'extracting'
                  ? 'Reading the questionnaire…'
                  : 'Answering from the library…'
              }
              description="This takes a minute or two for a long form. The page updates by itself."
            />
          ) : items.length ? (
            <>
              <Toolbar className="sticky top-0 z-10 rounded-md">
                <ToolbarLeft>
                  <Select
                    aria-label="Show"
                    value={effective}
                    onChange={(e) => setFilter(e.target.value as Filter)}
                  >
                    <option value="attention">Needs a person ({attention.length})</option>
                    <option value="unanswered">Unanswered ({q.counts.unanswered})</option>
                    <option value="all">All questions ({items.length})</option>
                  </Select>
                </ToolbarLeft>
                <ToolbarRight>
                  <ToolbarCount>{shown.length.toLocaleString()} shown</ToolbarCount>
                </ToolbarRight>
              </Toolbar>
              {shown.length === 0 ? (
                <EmptyState
                  variant="no-match"
                  title="Nothing needs a person"
                  description={
                    q.stage === 'in_review'
                      ? 'Every answer is from the library or reviewed.'
                      : undefined
                  }
                />
              ) : null}
              {shown.map((item, i) => {
                // A heading where the section changes.
                const heading =
                  item.section && item.section !== shown[i - 1]?.section ? item.section : null
                return (
                  <div key={item.id} className="flex flex-col gap-2">
                    {heading ? (
                      <h3 className="mt-2 text-sm font-semibold text-secondary">{heading}</h3>
                    ) : null}
                    <ItemCard
                      key={`${item.answer_choice}|${item.answer_text}`}
                      questionnaireId={q.id}
                      item={item}
                      editable={editable}
                      onPickFromLibrary={setPicking}
                    />
                  </div>
                )
              })}
            </>
          ) : q.stage !== 'failed' ? (
            <EmptyState variant="no-data" title="No questions were found in this document" />
          ) : null}
        </div>
      </div>

      <LibraryPickerDrawer
        key={picking?.id ?? 'closed'}
        questionnaireId={q.id}
        item={picking}
        onClose={() => setPicking(null)}
      />
      <Modal
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        tone="danger"
        title="Delete this questionnaire?"
        description="Its questions and answers are deleted. The answer library is not changed."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(q.id, {
                  onSuccess: () => navigate({ to: '/regulatory/questionnaires' }),
                  onError: (err) =>
                    toast.danger(err instanceof Error ? err.message : 'Delete failed'),
                })
              }
            >
              Delete
            </Button>
          </>
        }
      />
    </div>
  )
}
